import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  App,
  Button,
  Descriptions,
  Empty,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Skeleton,
  Space,
  Tabs,
  Typography,
  type TableColumnsType,
} from 'antd';
import { ChevronLeft, Edit3, Plus, Trash2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import {
  iamApi,
  type GetPolicyResponse,
  type IamPolicyGrantRecord,
  type IamUserGroupMember,
} from '@/api/iam.api';
import ProTable from '@/components/pro/ProTable';
import { fmtTime } from '@/utils/utils';
import {
  useAddUserToGroup,
  useDeleteIamUserGroup,
  useIamGroup,
  useIamGroupPolicyGrants,
  useUpdateIamUserGroup,
} from '@/pages/iam/composables/useIamGroups';
import { useIamUserList } from '@/pages/iam/composables/useIamUsers';
import { useRevokePolicyGrant } from '@/pages/iam/composables/useGrantPolicyData';
import { GrantPermissionDrawer } from '@/pages/iam/components';
import { PolicyPreviewDrawer, type PolicyPreviewData } from '@/pages/iam/components/PolicySelector';
import type { IamUserListItem, ListIAMUsersRequest } from '@/types/iam.types';
import './groups.css';

const ALL_USERS_REQUEST: ListIAMUsersRequest = {
  page: 1,
  limit: -1,
  order_by: ['-created_at'],
  name: '',
  display_name: '',
};

interface EditFormValues {
  name: string;
  description?: string;
}

function groupDisplayName(group: { name: string; display_name: string }): string {
  return group.display_name || group.name;
}

function userDisplayName(user: Pick<IamUserListItem, 'name'>): string {
  return user.name;
}

/** IAM 授权时间戳以秒为单位；仅在展示时转换。 */
function formatAuthorizationTime(timestamp: number): string {
  return fmtTime(new Date(timestamp * 1000).toISOString());
}

function toPolicyPreview(policy: GetPolicyResponse['data']): PolicyPreviewData {
  return {
    id: policy.policy_id,
    name: policy.name,
    description: policy.description,
    type: policy.type === 'system' ? 'SYSTEM' : 'CUSTOM',
    authorizationCount: policy.authorization_count,
    createdAt: new Date(policy.created_at * 1000).toISOString(),
    statements: policy.policy_config.statement.map((statement) => ({
      effect: statement.effect.toLowerCase() === 'deny' ? 'Deny' : 'Allow',
      actions: statement.action,
      resourceScope:
        statement.condition?.string_equals?.['inf:resourceOwner'] === '{self}' ? 'OWN' : 'ALL',
    })),
    permissionActions: policy.permission_summary.actions,
    source: policy.policy_config,
  };
}

export default function GroupDetail() {
  const { message } = App.useApp();
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: group, isLoading, error: groupError } = useIamGroup(id);
  const {
    data: grantsResponse,
    isLoading: areGrantsLoading,
    error: grantsError,
    refetch: refetchGrants,
  } = useIamGroupPolicyGrants(id);
  const updateGroup = useUpdateIamUserGroup();
  const addUserToGroup = useAddUserToGroup();
  const deleteGroup = useDeleteIamUserGroup();
  const revokePolicyGrant = useRevokePolicyGrant();

  const [memberOpen, setMemberOpen] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm] = Form.useForm<EditFormValues>();
  const [grantDrawerOpen, setGrantDrawerOpen] = useState(false);
  const [previewPolicyId, setPreviewPolicyId] = useState<string>();
  const [previewPolicyOpen, setPreviewPolicyOpen] = useState(false);
  const { data: usersData, isLoading: areUsersLoading } = useIamUserList(
    ALL_USERS_REQUEST,
    memberOpen
  );
  const policyDetailQuery = useQuery({
    queryKey: ['iam', 'policies', 'detail', previewPolicyId] as const,
    queryFn: async () => {
      const response = await iamApi.getPolicy(previewPolicyId as string);
      if (response.code !== 0) {
        throw new Error(response.message || '获取权限策略详情失败');
      }
      return response;
    },
    enabled: previewPolicyOpen && !!previewPolicyId,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: 'always',
    staleTime: 0,
  });

  const users = useMemo(() => usersData?.items ?? [], [usersData]);
  const members = useMemo(() => group?.edges.users ?? [], [group]);
  const grants = useMemo(() => grantsResponse?.data.records ?? [], [grantsResponse]);
  const previewPolicy = useMemo(
    () => (policyDetailQuery.data ? toPolicyPreview(policyDetailQuery.data.data) : undefined),
    [policyDetailQuery.data]
  );
  const memberIds = useMemo(() => new Set(members.map((user) => user.id)), [members]);

  const addSelectedMembers = async () => {
    if (!group || !selectedMembers.length) return;
    const userNameById = new Map(users.map((user) => [user.id, user.name]));
    try {
      await addUserToGroup.mutateAsync({
        group_id: group.id,
        add_users: selectedMembers.map((userId) => {
          const userName = userNameById.get(userId);
          if (!userName) throw new Error(`未找到用户 ${userId} 的名称`);
          return userName;
        }),
        remove_users: [],
      });
      message.success('用户已添加至用户组');
      setMemberOpen(false);
      setSelectedMembers([]);
    } catch {
      // 请求错误由全局请求层统一提示。
    }
  };

  const removeMember = async (user: IamUserGroupMember) => {
    if (!group) return;
    try {
      await addUserToGroup.mutateAsync({
        group_id: group.id,
        add_users: [],
        remove_users: [user.name],
      });
      message.success('用户已从用户组移除');
    } catch {
      // 请求错误由全局请求层统一提示。
    }
  };

  const saveBasic = async () => {
    if (!group) return;
    const values = await editForm.validateFields();
    try {
      await updateGroup.mutateAsync({
        id: group.id,
        display_name: values.name.trim(),
        comment: values.description?.trim() ?? '',
        users: members.map((user) => user.name),
      });
      message.success('用户组信息已更新');
      setEditOpen(false);
    } catch {
      // 请求错误由全局请求层统一提示。
    }
  };

  const removeGroup = () => {
    if (!group) return;
    deleteGroup.mutate(group.id, {
      onSuccess: () => {
        message.success('用户组已删除，成员未被删除，组授权已解除');
        navigate('/iam/groups');
      },
    });
  };

  const revokeGrant = async (grant: IamPolicyGrantRecord) => {
    try {
      await revokePolicyGrant.mutateAsync({
        policy_ids: [grant.policy_id],
        identities: [{ identity_type: grant.identity_type, identity: grant.identity }],
      });
      message.success('授权已解除');
    } catch {
      // 请求错误由全局请求层统一提示。
    }
  };

  const openGrantDrawer = () => {
    setGrantDrawerOpen(true);
  };

  const openPolicyPreview = (policyId: string) => {
    setPreviewPolicyId(policyId);
    setPreviewPolicyOpen(true);
  };

  const memberColumns: TableColumnsType<IamUserGroupMember> = [
    {
      title: '用户名 / ID',
      key: 'user',
      render: (_, user) => (
        <div className="iam-group-name-cell">
          <Button
            type="link"
            color="primary"
            className="iam-group-link"
            onClick={() => navigate(`/iam/users/${user.id}`)}
          >
            {user.name}
          </Button>
          <Typography.Text type="secondary">{user.id}</Typography.Text>
        </div>
      ),
    },
    {
      title: '添加时间',
      width: 180,
      render: (_, user) => fmtTime(user.joined_at),
    },
    {
      title: '操作',
      width: 100,
      fixed: 'right',
      render: (_, user) => (
        <Popconfirm
          title="从用户组移除？"
          description="移除后，用户将不再继承该组下的所有权限策略。"
          okText="确认移除"
          onConfirm={() => removeMember(user)}
        >
          <Button type="link" danger>
            移除
          </Button>
        </Popconfirm>
      ),
    },
  ];

  const grantColumns: TableColumnsType<IamPolicyGrantRecord> = [
    {
      title: '策略名称',
      key: 'policy',
      render: (_, grant) => (
        <div className="iam-group-name-cell">
          <Button
            type="link"
            className="iam-group-link"
            onClick={() => openPolicyPreview(grant.policy_id)}
          >
            {grant.policy_name}
          </Button>
          <Typography.Text type="secondary">{grant.policy_description}</Typography.Text>
        </div>
      ),
    },
    {
      title: '授权时间',
      width: 180,
      render: (_, grant) => formatAuthorizationTime(grant.authorization_at),
    },
    {
      title: '操作',
      width: 120,
      fixed: 'right',
      render: (_, grant) => (
        <Popconfirm
          title="解除用户组权限？"
          description={`解除后，组内 ${members.length} 名用户将不再通过本组获得该策略。`}
          okText="确认解除"
          okButtonProps={{ danger: true, loading: revokePolicyGrant.isPending }}
          onConfirm={() => revokeGrant(grant)}
        >
          <Button type="text" danger icon={<Trash2 size={14} />}>
            解除
          </Button>
        </Popconfirm>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className="pp-page iam-page mx-auto w-full max-w-[1200px]">
        <Skeleton active paragraph={{ rows: 10 }} />
      </div>
    );
  }
  if (!group) {
    return (
      <div className="pp-page iam-page mx-auto w-full max-w-[1200px]">
        <Empty
          description={
            groupError instanceof Error ? `加载用户组失败：${groupError.message}` : '未找到用户组'
          }
        />
      </div>
    );
  }

  const displayName = groupDisplayName(group);

  return (
    <div className="pp-page iam-page iam-group-detail-page mx-auto w-full max-w-[1200px]">
      <header className="pp-head">
        <Space align="center">
          <Button
            type="text"
            aria-label="返回用户组列表"
            icon={<ChevronLeft size={16} />}
            onClick={() => navigate('/iam/groups')}
          />
          <div className="iam-group-header">
            <h2>{displayName}</h2>
            <Typography.Text type="secondary">{group.id}</Typography.Text>
          </div>
        </Space>
        <Space wrap>
          <Button
            icon={<Edit3 size={14} />}
            onClick={() => {
              editForm.setFieldsValue({ name: displayName, description: group.comment });
              setEditOpen(true);
            }}
          >
            编辑
          </Button>
          <Popconfirm
            title="删除用户组？"
            description="删除后会移除组内关系并解除组授权，但不会删除用户。"
            okText="确认删除"
            okButtonProps={{ danger: true }}
            onConfirm={removeGroup}
          >
            <Button danger icon={<Trash2 size={14} />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      </header>

      <Descriptions
        bordered
        column={{ xs: 1, sm: 2, lg: 4 }}
        style={{ marginBottom: 16 }}
        items={[
          { key: 'name', label: '用户组名称', children: displayName },
          { key: 'count', label: '用户数量', children: members.length },
          { key: 'created', label: '创建时间', children: fmtTime(group.created_at) },
          { key: 'desc', label: '描述', span: 4, children: group.comment || '—' },
        ]}
      />

      <Tabs
        items={[
          {
            key: 'members',
            label: `用户列表（${members.length}）`,
            children: (
              <>
                <div className="iam-group-tab-actions">
                  <Button
                    type="primary"
                    icon={<Plus size={14} />}
                    onClick={() => {
                      setSelectedMembers([]);
                      setMemberOpen(true);
                    }}
                  >
                    添加用户
                  </Button>
                </div>
                <ProTable<IamUserGroupMember> data={members} rowKey="id" columns={memberColumns} />
              </>
            ),
          },
          {
            key: 'policies',
            label: `权限策略（${grantsResponse?.data.total ?? grants.length}）`,
            children: (
              <>
                <div className="iam-group-tab-actions">
                  <Button type="primary" icon={<Plus size={14} />} onClick={openGrantDrawer}>
                    新增授权
                  </Button>
                </div>
                <ProTable<IamPolicyGrantRecord>
                  data={grants}
                  loading={areGrantsLoading}
                  error={
                    grantsError
                      ? grantsError instanceof Error
                        ? grantsError.message
                        : String(grantsError)
                      : null
                  }
                  onRetry={() => void refetchGrants()}
                  rowKey={(grant) =>
                    JSON.stringify([grant.identity_type, grant.identity, grant.policy_id])
                  }
                  columns={grantColumns}
                />
              </>
            ),
          },
        ]}
      />

      <Modal
        title="添加用户"
        open={memberOpen}
        okText="确认添加"
        okButtonProps={{ disabled: !selectedMembers.length }}
        confirmLoading={addUserToGroup.isPending}
        onOk={addSelectedMembers}
        onCancel={() => {
          setMemberOpen(false);
          setSelectedMembers([]);
        }}
      >
        <Select
          mode="multiple"
          showSearch
          optionFilterProp="label"
          style={{ width: '100%' }}
          loading={areUsersLoading}
          placeholder="选择一个或多个用户"
          value={selectedMembers}
          onChange={setSelectedMembers}
          options={users.map((user) => ({
            value: user.id,
            label: `${userDisplayName(user)}（${user.id}）`,
            disabled: memberIds.has(user.id),
          }))}
        />
      </Modal>

      <Modal
        title="编辑用户组"
        open={editOpen}
        okText="保存"
        onOk={saveBasic}
        onCancel={() => setEditOpen(false)}
        destroyOnHidden
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            name="name"
            label="用户组名称"
            rules={[
              { required: true, message: '请输入用户组名称' },
              { min: 1, max: 64, message: '名称长度为 1-64 个字符' },
              {
                pattern: /^[一-龥A-Za-z0-9_-]+$/,
                message: '仅支持中英文、数字、连字符和下划线',
              },
            ]}
          >
            <Input placeholder="例如 Agent_开发组" />
          </Form.Item>
          <Form.Item name="description" label="描述">
            <Input.TextArea rows={3} maxLength={200} showCount placeholder="说明用户组职责范围" />
          </Form.Item>
        </Form>
      </Modal>

      <GrantPermissionDrawer
        open={grantDrawerOpen}
        preset={{ principalType: 'GROUP', principalId: group.id }}
        onClose={() => setGrantDrawerOpen(false)}
        onSuccess={() => void refetchGrants()}
      />

      <PolicyPreviewDrawer
        open={previewPolicyOpen}
        policy={previewPolicy}
        loading={policyDetailQuery.isFetching}
        error={
          policyDetailQuery.error
            ? policyDetailQuery.error instanceof Error
              ? policyDetailQuery.error.message
              : String(policyDetailQuery.error)
            : undefined
        }
        onClose={() => setPreviewPolicyOpen(false)}
        afterClose={() => setPreviewPolicyId(undefined)}
        onViewDetail={(policy) => navigate(`/iam/policies/${policy.id}`)}
      />
    </div>
  );
}
